import { useSetAtom } from "jotai";
import { useMemo } from "react";
import type { AnswerChangeEvent } from "../../../components/Questionnaire/Section/Question/QuestionFactory";
import type { QuestionProps } from "../../../components/Questionnaire/Section/Question/types";
import type { SectionProps } from "../../../components/Questionnaire/Section";
import type { RuleSet } from "../../../components/Questionnaire/validation/form/types";
import { ACTIONS } from "../reducer/questionnaireReducer";
import { questionnaireReducerAtom } from "../state/questionnaireReducerAtom";
import type {
  AddressMode,
  PartialSection,
  QuestionnaireAction,
  UpdateAnswerPayload,
} from "../types";

type DispatchFn = (action: QuestionnaireAction) => void;

const createSetSections =
  (dispatch: DispatchFn) => (sections: SectionProps[]) => {
    dispatch({ type: ACTIONS.SET, payload: sections });
  };

const createSetValidations =
  (dispatch: DispatchFn) => (validations: readonly RuleSet[]) => {
    dispatch({ type: ACTIONS.SET_VALIDATIONS, payload: validations });
  };

const createSetConfig =
  (dispatch: DispatchFn) => (config: Record<string, unknown>) => {
    dispatch({ type: ACTIONS.SET_CONFIG, payload: config });
  };

const createSetErrors =
  (dispatch: DispatchFn) => (errors: Record<string, string>) => {
    dispatch({ type: ACTIONS.SET_ERRORS, payload: errors });
  };

const createSetEditMode = (dispatch: DispatchFn) => (isEditing: boolean) => {
  dispatch({ type: ACTIONS.SET_EDIT_MODE, payload: isEditing });
};

const createSetAddressMode =
  (dispatch: DispatchFn) => (name: string, mode: AddressMode) => {
    dispatch({ type: ACTIONS.SET_ADDRESS_MODE, payload: { name, mode } });
  };

const createMerge =
  (dispatch: DispatchFn) =>
  (sections: PartialSection[], requestStartTime: number) => {
    dispatch({
      type: ACTIONS.MERGE,
      payload: { sections, requestStartTime },
    });
  };

const createUpdateAnswer =
  (dispatch: DispatchFn) => (event: AnswerChangeEvent) => {
    const { target } = event;
    const { name, value } = target as UpdateAnswerPayload;
    dispatch({ type: ACTIONS.UPDATE_ANSWER, payload: { name, value } });
  };

const createNavigatePrev = (dispatch: DispatchFn) => () => {
  dispatch({ type: ACTIONS.PREVIOUS_SECTION });
};

const createNavigateNext = (dispatch: DispatchFn) => () => {
  dispatch({ type: ACTIONS.NEXT_SECTION });
};

const createValidateAction = (dispatch: DispatchFn) => () => {
  dispatch({ type: ACTIONS.VALIDATE_FORM });
};

const createRemoveError = (dispatch: DispatchFn) => (questionName: string) => {
  dispatch({ type: ACTIONS.REMOVE_ERROR, payload: questionName });
};

const createSetError =
  (dispatch: DispatchFn) => (name: string, message: string) => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: { name, message } });
  };

const createSetAsyncError =
  (dispatch: DispatchFn) => (name: string, message: string) => {
    dispatch({ type: ACTIONS.SET_ASYNC_ERROR, payload: { name, message } });
  };

const createClearAsyncError = (dispatch: DispatchFn) => (name: string) => {
  dispatch({ type: ACTIONS.CLEAR_ASYNC_ERROR, payload: name });
};

const createShowQuestion = (dispatch: DispatchFn) => (questionName: string) => {
  dispatch({ type: ACTIONS.SHOW_QUESTION, payload: questionName });
};

const createEditQuestion =
  (dispatch: DispatchFn) =>
  ({
    questionName,
    assumptionGroupIndex,
  }: {
    questionName: string;
    assumptionGroupIndex?: number;
  }) => {
    dispatch({
      type: ACTIONS.EDIT_QUESTION,
      payload: { questionName, assumptionGroupIndex },
    });
  };

const createAppendQuestions =
  (dispatch: DispatchFn) => (questions: QuestionProps[]) => {
    dispatch({ type: ACTIONS.APPEND_QUESTIONS, payload: questions });
  };

const createConfirmAssumptionGroup =
  (dispatch: DispatchFn) => (payload: number) => {
    dispatch({ type: ACTIONS.CONFIRM_ASSUMPTION_GROUP, payload });
  };

const createSetCurrentSectionIndex =
  (dispatch: DispatchFn) => (sectionIndex: number) => {
    dispatch({ type: ACTIONS.SET_CURRENT_SECTION, payload: sectionIndex });
  };

const createSetVisible =
  (dispatch: DispatchFn) => (questionNames: string[]) => {
    dispatch({ type: ACTIONS.SET_VISIBLE, payload: questionNames });
  };

const createSetHidden = (dispatch: DispatchFn) => (questionNames: string[]) => {
  dispatch({ type: ACTIONS.SET_HIDDEN, payload: questionNames });
};

/**
 * All questionnaire action dispatchers. No state subscription - never re-renders from state changes.
 *
 * @example const { updateAnswer, navigateNext, setError } = useQuestionnaireActions();
 * @see useQuestionnaire - full state + actions (re-renders on changes)
 */
export const useQuestionnaireActions = () => {
  const dispatch = useSetAtom(questionnaireReducerAtom);

  return useMemo(
    () => ({
      setSections: createSetSections(dispatch),
      setValidations: createSetValidations(dispatch),
      setConfig: createSetConfig(dispatch),
      setErrors: createSetErrors(dispatch),
      setEditMode: createSetEditMode(dispatch),
      setAddressMode: createSetAddressMode(dispatch),
      merge: createMerge(dispatch),
      updateAnswer: createUpdateAnswer(dispatch),
      navigatePrev: createNavigatePrev(dispatch),
      navigateNext: createNavigateNext(dispatch),
      validateAction: createValidateAction(dispatch),
      removeError: createRemoveError(dispatch),
      setError: createSetError(dispatch),
      setAsyncError: createSetAsyncError(dispatch),
      clearAsyncError: createClearAsyncError(dispatch),
      showQuestion: createShowQuestion(dispatch),
      editQuestion: createEditQuestion(dispatch),
      appendQuestions: createAppendQuestions(dispatch),
      confirmAssumptionGroup: createConfirmAssumptionGroup(dispatch),
      setCurrentSectionIndex: createSetCurrentSectionIndex(dispatch),
      setVisible: createSetVisible(dispatch),
      setHidden: createSetHidden(dispatch),
    }),
    [dispatch],
  );
};
